import { CommonModule } from "@angular/common";
import { Component, Input, OnChanges } from "@angular/core";
import { BookingStatus } from "src/app/core/constants/bookingConstant";
import { historyServerApiColletions } from "src/app/core/constants/api_collection";
import { Helper } from "src/app/core/helpers/helper";
import { ApiService } from "src/app/shared/services/api.service";
import { HistoryService } from "src/app/shared/services/history.service";
import { SHOW_ADMIN_LOGS } from "src/app/layouts/sidebar/menu";

export interface TripLogEntry {
  time: number;
  actor: string;
  message: string;
  kind: string;
}

const STATUS_MESSAGE: Record<number, string> = {
  [BookingStatus.REQUESTED]: "Trip requested",
  [BookingStatus.ASSIGNED]: "Driver assigned",
  [BookingStatus.NO_DRIVER_FOUND]: "No driver found",
  [BookingStatus.NOT_ANSWERED]: "Offer not answered",
  [BookingStatus.REJECTED]: "Driver rejected the offer",
  [BookingStatus.ACCEPTED]: "Driver accepted",
  [BookingStatus.IN_ROUTE]: "Driver en route",
  [BookingStatus.ARRIVED_AT_PICKUP]: "Driver arrived at pickup",
  [BookingStatus.PICKED]: "Passenger picked up",
  [BookingStatus.STARTED]: "Trip started",
  [BookingStatus.ARRIVED_AT_STOP]: "Arrived at a stop",
  [BookingStatus.ARRIVED_AT_DESTINATION]: "Arrived at destination",
  [BookingStatus.COMPLETED]: "Trip completed",
  [BookingStatus.CANCELLED]: "Trip cancelled",
  [BookingStatus.MERCHANT_REJECTED]: "Merchant rejected the trip"
};

const ACTOR: Record<number, string> = {
  1: "Admin",
  2: "Customer",
  3: "Driver",
  4: "Partner",
  5: "Dispatcher",
  6: "Corporate"
};

@Component({
  selector: "app-trip-log-timeline",
  standalone: true,
  imports: [CommonModule],
  templateUrl: "./trip-log-timeline.component.html",
  styleUrl: "./trip-log-timeline.component.scss"
})
export class TripLogTimelineComponent implements OnChanges {
  @Input() trip: any = null;

  entries: TripLogEntry[] = [];
  loading = false;
  note = "";
  adminNote = "";

  constructor(
    private history: HistoryService,
    private api: ApiService,
    public helper: Helper
  ) {}

  ngOnChanges(): void {
    this.load();
  }

  when(time: number): string {
    if (!time) return "Time not stored";
    try {
      const zoned = this.helper.moment(time).tz(this.helper.displayTimezone);
      return `${zoned.format("DD-MM-YY")} ${zoned.format(this.helper.getTimeFormat?.(null) || "HH:mm")}`;
    } catch {
      return String(time);
    }
  }

  private load(): void {
    this.entries = [];
    this.note = "";
    this.adminNote = "";
    const id = this.trip?._id;
    const uniqueId = this.trip?.uniqueId;
    if (!id && uniqueId == null) return;
    this.loading = true;
    const local = this.fromBooking(this.trip);
    if (!id) {
      this.finish(local, uniqueId);
      return;
    }
    this.history.getRunningBookingDetails(id).subscribe((running: any) => {
      const booking = running?.booking;
      if (booking?.statusTimeline) {
        this.finish(this.fromBooking(booking), booking.uniqueId ?? uniqueId, booking);
        return;
      }
      this.history.getBookingDetails(id).subscribe((historyRow: any) => {
        const past = historyRow?.booking;
        this.finish(past?.statusTimeline ? this.fromBooking(past) : local, past?.uniqueId ?? uniqueId, past || this.trip);
      });
    });
  }

  private finish(base: TripLogEntry[], uniqueId: any, booking?: any): void {
    const tripNo = String(uniqueId ?? "");
    this.history.getExceptions({ isShowErrorToast: false, isShowLoader: false }).subscribe((body: any) => {
      const rows = body?.data?.exceptions || [];
      const matched = tripNo ? rows.filter((row: any) => String(row?.uniqueId ?? "") === tripNo) : [];
      matched.forEach((row: any) => {
        base.push({
          time: Number(row.timestamp || row.bookingTime || 0),
          actor: "System",
          kind: "Exception",
          message: String(row.exceptionType || "Exception").replace(/_/g, " ")
        });
      });
      if (booking?.customerDetail?.isBlocked || this.trip?.customerDetail?.isBlocked) {
        base.push({
          time: 0,
          actor: "System",
          kind: "Block",
          message: "Customer is on the block list."
        });
      }
      if (!SHOW_ADMIN_LOGS) {
        this.adminNote = "";
        this.publish(base);
        return;
      }
      const typeId = booking?._id || this.trip?._id;
      if (!typeId) {
        this.publish(base);
        return;
      }
      this.fetchChangeLogs(typeId).then((rows) => {
        rows.forEach((row) => {
          base.push({
            time: new Date(row.createdAt).getTime() || 0,
            actor: row.entityDetail?.name || "Admin",
            kind: String(row.changeLogType || "Activity").replace(/_/g, " "),
            message: `${row.action || "UPDATE"} ${row.changeLogType || ""}`.trim()
          });
        });
        this.publish(base);
      });
    });
  }

  private publish(base: TripLogEntry[]): void {
    this.entries = base.sort((a, b) => (b.time || 0) - (a.time || 0));
    this.loading = false;
  }

  private fetchChangeLogs(typeId: string): Promise<any[]> {
    const one = (changeLogType: string) =>
      new Promise<any[]>((resolve) => {
        this.api
          .get({
            url: historyServerApiColletions.changeLogs,
            parameters: { typeId, changeLogType, limit: 50, page: 1, isShowErrorToast: false, isShowLoader: false }
          })
          .subscribe({
            next: (response) => resolve(response?.data?.data?.changeLogs || []),
            error: (err) => {
              if (err?.status === 401 || err?.status === 403) {
                this.adminNote = "";
              }
              resolve([]);
            }
          });
      });
    return Promise.all([one("BOOKING"), one("BOOKING_HISTORY")]).then(([a, b]) => [...a, ...b]);
  }

  private fromBooking(booking: any): TripLogEntry[] {
    const timeline = Array.isArray(booking?.statusTimeline) ? booking.statusTimeline : [];
    const entries: TripLogEntry[] = [];
    timeline.forEach((row: any) => {
      const status = Number(row.status);
      const actor = row.name || ACTOR[Number(row.type)] || "System";
      const message = STATUS_MESSAGE[status] || `Status ${status}`;
      const note = row.note ? ` ${row.note}` : "";
      entries.push({ time: Number(row.time || 0), actor, kind: "Status", message: message + note });
      const offered = Array.isArray(row.entityDetails) ? row.entityDetails : [];
      if (offered.length) {
        entries.push({
          time: Number(row.time || 0),
          actor,
          kind: "Offer",
          message: `Offer sent to ${offered.map((item: any) => item.name || item.uniqueId || "driver").join(", ")}`
        });
      } else if (Array.isArray(row.driverIds) && row.driverIds.length && status === BookingStatus.ASSIGNED) {
        entries.push({
          time: Number(row.time || 0),
          actor,
          kind: "Offer",
          message: `Offer sent to ${row.driverIds.length} driver${row.driverIds.length === 1 ? "" : "s"}`
        });
      }
      const rejected = Array.isArray(row.rejectedDriverIds) ? row.rejectedDriverIds : [];
      if (rejected.length && status !== BookingStatus.REJECTED) {
        entries.push({
          time: Number(row.time || 0),
          actor: "Driver",
          kind: "Rejection",
          message: `${rejected.length} driver${rejected.length === 1 ? "" : "s"} rejected the offer`
        });
      }
    });
    return entries;
  }
}
